import { Link } from 'react-router-dom'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { Calendar } from 'lucide-react'

import {
    DataTableColumn,
    FilterItemConfig,
    FilterToggle,
} from '@/components/ui/data-table-customized'
import { Badge } from '@/components/ui/badge'
import { Rack } from '../../types/response'
import { paths } from '@/utils/path'
import { RACK_STATUS, RACK_STATUS_VARIANTS } from '../../constants'

dayjs.extend(relativeTime)

// === Columns ===

export const getRackColumns = (): DataTableColumn<Rack>[] => [
    {
        key: 'name',
        title: 'Name',
        width: 200,
        pinned: 'left',
        render: (value, row) => (
            <Link
                to={`${paths.warehouseRacks}/${row.id}`}
                className="text-blue-600 hover:underline font-medium"
            >
                {value as string}
            </Link>
        ),
    },
    {
        key: 'code',
        title: 'Code',
        width: 140,
        render: (value) => (
            <span className="text-sm font-mono">
                {(value as string) || '-'}
            </span>
        ),
    },
    {
        key: 'location',
        title: 'Location',
        width: 200,
        render: (_value, row) => (
            <span className="text-sm">{row.location?.name || '-'}</span>
        ),
    },
    {
        key: 'status',
        title: 'Status',
        width: 150,
        render: (_value, row) => {
            const variant =
                (RACK_STATUS_VARIANTS[row.status] as
                    | 'outline'
                    | 'secondary'
                    | 'default'
                    | 'destructive') || 'outline'
            return (
                <Badge variant={variant} className="capitalize">
                    {row.status?.replace('_', ' ')}
                </Badge>
            )
        },
    },
    {
        key: 'u_height',
        title: 'U Height',
        width: 100,
        render: (value) => <span className="text-sm">{value as number}U</span>,
    },
    {
        key: 'power_supply',
        title: 'Power (W)',
        width: 120,
        render: (value) => (
            <span className="text-sm">{(value as number) || 0}</span>
        ),
    },
    {
        key: 'bandwidth_limit',
        title: 'Bandwidth Limit',
        width: 150,
        render: (value) => (
            <span className="text-sm">{(value as number) || 0} Mbps</span>
        ),
    },
    {
        key: 'bandwidth_reality',
        title: 'Bandwidth Usage',
        width: 150,
        render: (value) => (
            <span className="text-sm">{(value as number) || 0} Mbps</span>
        ),
    },
    {
        key: 'started_at',
        title: 'Started At',
        width: 150,
        render: (value) => {
            if (!value) return <span className="text-sm">-</span>
            const date = dayjs(value as string)
            return (
                <div
                    className="flex items-center gap-2"
                    title={date.format('YYYY-MM-DD HH:mm:ss')}
                >
                    <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-sm">{date.format('YYYY-MM-DD')}</span>
                </div>
            )
        },
    },
    {
        key: 'description',
        title: 'Description',
        width: 200,
        render: (value) => (
            <span className="text-sm truncate block max-w-[200px]">
                {(value as string) || '-'}
            </span>
        ),
    },
    {
        key: 'created_at',
        title: 'Created',
        width: 150,
        render: (value) => {
            const date = dayjs(value as string)
            return (
                <div
                    className="flex items-center gap-2"
                    title={date.format('YYYY-MM-DD HH:mm:ss')}
                >
                    <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-sm">{date.fromNow()}</span>
                </div>
            )
        },
    },
]

// === Filters ===

const rackStatusOptions = Object.entries(RACK_STATUS).map(([_key, value]) => ({
    value,
    label: value
        .split('_')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' '),
}))

export const getRackFilterItems = (): FilterItemConfig[] => [
    {
        type: 'select',
        name: 'status',
        label: 'Status',
        placeholder: 'Filter by status',
        options: rackStatusOptions,
    },
]

// === Filter Toggles (column visibility) ===

export const getRackFilterToggles = (): FilterToggle[] => [
    { key: 'name', label: 'Name' },
    { key: 'code', label: 'Code' },
    { key: 'location', label: 'Location' },
    { key: 'status', label: 'Status' },
    { key: 'u_height', label: 'U Height' },
    { key: 'power_supply', label: 'Power (W)' },
    { key: 'bandwidth_limit', label: 'Bandwidth Limit' },
    { key: 'bandwidth_reality', label: 'Bandwidth Usage' },
    { key: 'started_at', label: 'Started At' },
    { key: 'description', label: 'Description' },
    { key: 'created_at', label: 'Created' },
]
